import type { SpectrumConfig, ResolvedConfig } from '../types/config.js';

/** Pull and trim the user-supplied `api` URL; returns undefined when missing or whitespace-only. */
function getApiInput(config: SpectrumConfig): string | undefined {
  return config.api?.trim();
}

/** Validate and normalize a full Spectrum API URL into `baseUrl`. The entire URL — including any path prefix — is preserved; endpoint paths are appended to it. */
function parseApiInput(api: string): Pick<ResolvedConfig, 'baseUrl'> {
  const trimmedApi = api.trim();

  if (!/^https?:\/\//i.test(trimmedApi)) {
    throw new Error('api must be a full URL');
  }

  return { baseUrl: new URL(trimmedApi).toString() };
}

/**
 * Validate user input and apply defaults to produce a `ResolvedConfig`.
 *
 * Throws:
 * - `Error('api is required')` if `api` is missing or blank
 * - `Error('api must be a full URL')` if `api` is not http/https
 */
export function resolveConfig(config: SpectrumConfig): ResolvedConfig {
  const api = getApiInput(config);
  if (!api) {
    throw new Error('api is required');
  }

  const { baseUrl } = parseApiInput(api);

  return {
    baseUrl,
    defaultChain: config.defaultChain,
    timeout: config.timeout ?? 30_000,
    retries: config.retries ?? 3,
    cache: {
      enabled: config.cache?.enabled ?? true,
      ttl: config.cache?.ttl ?? 5_000,
      maxEntries: config.cache?.maxEntries ?? 1024,
      overrides: config.cache?.overrides ?? {},
    },
    logging: {
      level: config.logging?.level ?? 'warn',
    },
    hooks: config.hooks ?? {},
  };
}
